import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import { eq } from 'tio-ui/utilities';
import StatCard from 'tio-employee/components/reimagined/stat-card';
import SectionHeader from 'tio-employee/components/reimagined/section-header';
import {
  PhBank,
  PhTrendUp,
  PhCalendar,
  PhLightbulb,
  PhArrowRight,
  PhSparkle,
  PhCurrencyDollar,
  PhPercent,
  PhHandshake,
  PhTarget,
  PhChartLineUp,
  PhWarning,
  PhChatCircleDots,
  PhGraduationCap,
  PhMagnifyingGlass,
  PhBookOpen,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import DebtJourneyHero from 'tio-employee/components/reimagined/debt-journey-hero';
import LoanProgressCard from 'tio-employee/components/reimagined/loan-progress-card';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import MilestoneTracker from 'tio-employee/components/reimagined/milestone-tracker';
import type { DebtMilestone } from 'tio-employee/components/reimagined/milestone-tracker';
import SmartActionCard from 'tio-employee/components/reimagined/smart-action-card';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import ToolCard from 'tio-employee/components/reimagined/tool-card';
import type {
  LrapDiscoverySummary,
  EmployerContributions,
} from 'tio-employee/services/career-pathways-api';
import type { DataSourceInfo, LiveSection127Info } from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';

interface LoansIndexModel {
  loans: LoanAccount[];
  hasLinkedLoans: boolean;
  isDemoMode: boolean;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  section127Info: LiveSection127Info;
  hasStrategyFinderResults: boolean;
}

interface LoansIndexSignature {
  Args: {
    model: LoansIndexModel;
  };
}

// Demo loan data
const DEMO_LOANS: LoanAccount[] = [
  {
    id: '1',
    servicer: 'MOHELA',
    balance: 32450,
    originalBalance: 48000,
    interestRate: 5.2,
    monthlyPayment: 285,
    nextPaymentDate: '2026-02-15',
    estimatedPayoff: '2028-03-15',
    isReceivingEmployerHelp: true,
    loanCount: 4,
    accountType: 'federal',
  },
  {
    id: '2',
    servicer: 'Nelnet',
    balance: 12780,
    originalBalance: 22000,
    interestRate: 6.8,
    monthlyPayment: 102,
    nextPaymentDate: '2026-02-20',
    estimatedPayoff: '2029-08-15',
    isReceivingEmployerHelp: false,
    loanCount: 2,
    accountType: 'federal',
  },
];

// Demo milestones
const DEMO_MILESTONES: DebtMilestone[] = [
  {
    id: '1',
    title: 'First $5k',
    description: 'Paid first $5,000',
    threshold: 7,
    achieved: true,
    achievedDate: 'Mar 2024',
    icon: 'rocket',
  },
  {
    id: '2',
    title: '25% Done',
    description: 'Quarter paid off',
    threshold: 25,
    achieved: true,
    achievedDate: 'Sep 2024',
    icon: 'star',
  },
  {
    id: '3',
    title: 'Halfway',
    description: '50% to freedom',
    threshold: 50,
    achieved: false,
    icon: 'target',
  },
  {
    id: '4',
    title: 'Under $20k',
    description: 'Below $20,000',
    threshold: 71,
    achieved: false,
    icon: 'medal',
  },
  {
    id: '5',
    title: 'Debt Free!',
    description: '$0 balance',
    threshold: 100,
    achieved: false,
    icon: 'crown',
  },
];

// Smart actions for debt management - static structure, dynamic content via getter
const createDebtActions = (options: {
  section127Remaining: number;
  section127Used: number;
  hasStrategyFinderResults: boolean;
  formatCurrency: (amount: number) => string;
}): SmartAction[] => {
  const { section127Remaining, section127Used, hasStrategyFinderResults, formatCurrency } = options;

  const actions: SmartAction[] = [];

  // Strategy Finder action - higher priority if user hasn't run it
  actions.push({
    id: '1',
    priority: hasStrategyFinderResults ? 'medium' : 'high',
    category: 'money',
    icon: PhPercent,
    title: hasStrategyFinderResults
      ? 'Update your repayment strategy'
      : 'Find your optimal repayment strategy',
    description: hasStrategyFinderResults
      ? "Life changes. Review your strategy to ensure you're still on the best path to debt freedom."
      : "Based on your employment and loan profile, we can find the best path to debt freedom—whether that's PSLF, IDR, or aggressive payoff.",
    impact: 'Potentially save thousands',
    cta: hasStrategyFinderResults ? 'Review Strategy' : 'Run Strategy Finder',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  });

  // Section 127 action - only show if there's remaining balance
  if (section127Remaining > 0) {
    actions.push({
      id: '2',
      priority: section127Remaining > 1000 ? 'high' : 'medium',
      category: 'opportunity',
      icon: PhHandshake,
      title: `${formatCurrency(section127Remaining)} of Section 127 unused`,
      description: `You've used ${formatCurrency(section127Used)} of your $5,250 tax-free benefit this year. Allocating the rest to loans could accelerate payoff.`,
      impact: 'Pay off faster',
      cta: 'Optimize Allocation',
      route: 'authenticated.reimagined.finance.optimizer',
    });
  }

  // AI Advisor action
  actions.push({
    id: '3',
    priority: 'medium',
    category: 'opportunity',
    icon: PhSparkle,
    title: 'Ask the AI Loan Advisor',
    description:
      'Get personalized guidance on PSLF, IDR plans, refinancing decisions, and repayment optimization from our AI expert.',
    impact: 'Expert advice instantly',
    cta: 'Chat with Advisor',
    route: 'authenticated.reimagined.plan.advisor',
  });

  return actions;
};

export default class CareerPathwaysLoansIndex extends Component<LoansIndexSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare sessionContext: SessionContextService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  /**
   * Data source for student loan sections
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for employer contributions
   */
  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for LRAP discovery
   */
  get lrapSource(): DataSource {
    // LRAP discovery is currently demo/placeholder
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for AI recommendations/smart actions
   */
  get recommendationsSource(): DataSource {
    // AI recommendations are placeholder until AI service built
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for debt milestones
   */
  get milestonesSource(): DataSource {
    // Milestones are calculated from loan data
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get showLoans() {
    return this.isDemoMode ? this.scenario.features.studentLoanRepayment : true;
  }

  get isFirstLogin() {
    return this.isDemoMode ? this.scenario.userState.isFirstLogin : false;
  }

  get hasLinkedLoans() {
    return this.isDemoMode
      ? this.scenario.userState.hasLinkedLoans
      : this.args.model.hasLinkedLoans;
  }

  get loans() {
    return this.isDemoMode ? DEMO_LOANS : this.args.model.loans;
  }

  get milestones(): DebtMilestone[] {
    // In demo mode, use static milestones
    if (this.isDemoMode) {
      return DEMO_MILESTONES;
    }

    // Calculate milestones dynamically from real loan data
    const totalOriginal = this.totalOriginalBalance;
    const totalCurrent = this.totalBalance;

    if (totalOriginal <= 0) {
      return DEMO_MILESTONES;
    }

    const paidOff = totalOriginal - totalCurrent;
    const paidPercent = (paidOff / totalOriginal) * 100;

    return [
      {
        id: '1',
        title: 'First $5k',
        description: 'Paid first $5,000',
        threshold: totalOriginal > 5000 ? (5000 / totalOriginal) * 100 : 50,
        achieved: paidOff >= 5000,
        achievedDate: paidOff >= 5000 ? 'Achieved!' : undefined,
        icon: 'rocket',
      },
      {
        id: '2',
        title: '25% Done',
        description: 'Quarter paid off',
        threshold: 25,
        achieved: paidPercent >= 25,
        achievedDate: paidPercent >= 25 ? 'Achieved!' : undefined,
        icon: 'star',
      },
      {
        id: '3',
        title: 'Halfway',
        description: '50% to freedom',
        threshold: 50,
        achieved: paidPercent >= 50,
        icon: 'target',
      },
      {
        id: '4',
        title: '75% Done',
        description: 'Three quarters there',
        threshold: 75,
        achieved: paidPercent >= 75,
        icon: 'medal',
      },
      {
        id: '5',
        title: 'Debt Free!',
        description: '$0 balance',
        threshold: 100,
        achieved: totalCurrent <= 0,
        icon: 'crown',
      },
    ];
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get hasStrategyFinderResults() {
    return this.isDemoMode ? false : this.args.model.hasStrategyFinderResults;
  }

  get actions() {
    // In demo mode, use demo values
    if (this.isDemoMode) {
      return createDebtActions({
        section127Remaining: 1200,
        section127Used: 4050,
        hasStrategyFinderResults: false,
        formatCurrency: this.formatCurrency,
      });
    }

    // Use live data
    return createDebtActions({
      section127Remaining: this.section127Info.remaining,
      section127Used: this.section127Info.yearToDateUsed,
      hasStrategyFinderResults: this.hasStrategyFinderResults,
      formatCurrency: this.formatCurrency,
    });
  }

  get totalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalOriginalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.originalBalance, 0);
  }

  get totalMonthlyPayment() {
    return this.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get progressPercent() {
    if (this.totalOriginalBalance <= 0) return 0;
    const paid = this.totalOriginalBalance - this.totalBalance;
    return Math.round((paid / this.totalOriginalBalance) * 100);
  }

  get yearProgressAmount() {
    if (this.isDemoMode) return 4850;
    // For live data, estimate YTD progress from employer contributions
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      // Include employer contributions as part of year progress
      return contributions.contributions.yearToDate;
    }
    const paid = this.totalOriginalBalance - this.totalBalance;
    return paid > 0 ? paid : 0;
  }

  get yearOverYearImprovement() {
    // Calculate year-over-year improvement percentage
    if (this.isDemoMode) return 34;
    // For live data, estimate based on progress
    if (this.totalOriginalBalance <= 0) return 0;
    const progressPct = this.progressPercent;
    // Estimate improvement based on how much faster we're paying off vs original schedule
    return Math.min(Math.round(progressPct * 0.5), 50);
  }

  get estimatedPayoffDate() {
    if (this.isDemoMode) return '2029-08-15';
    // Calculate from loan data
    if (this.loans.length === 0) return new Date().toISOString();
    // Find the latest estimated payoff from all loans
    const payoffDates = this.loans
      .map((loan) => loan.estimatedPayoff)
      .filter((date) => date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return payoffDates[0] || new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString();
  }

  get employerContribution() {
    if (this.isDemoMode) return 125;
    // Get monthly contribution from employer
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      // Estimate monthly from YTD
      const currentMonth = new Date().getMonth() + 1;
      return Math.round(contributions.contributions.yearToDate / currentMonth);
    }
    return 0;
  }

  get averageInterestRate() {
    if (!this.loans.length) return 0;
    const weighted = this.loans.reduce((sum, loan) => sum + loan.balance * loan.interestRate, 0);
    const total = this.loans.reduce((sum, loan) => sum + loan.balance, 0);
    return total > 0 ? Number((weighted / total).toFixed(1)) : 0;
  }

  get nextPaymentSubtext() {
    if (this.isDemoMode) return 'Next due: Feb 15';
    const next = this.loans
      .map((loan) => loan.nextPaymentDate)
      .filter((date) => date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!next) return 'Next due: —';
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(next));
    return `Next due: ${formatted}`;
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  // LRAP Discovery Integration
  get lrapDiscovery(): LrapDiscoverySummary | null {
    if (!this.isDemoMode) return null;
    // Fetch LRAP discovery data for banner
    const clientId = getCurrentDemoClientId();
    // Only show LRAP banner for certain demo clients
    if (clientId === 'legal' || clientId === 'advocate') {
      return this.careerPathwaysApi.fetchLrapDiscoverySyncForDemo();
    }
    return null;
  }

  get hasLrapOpportunity() {
    const discovery = this.lrapDiscovery;
    if (!discovery) return false;
    return (
      discovery.eligiblePrograms.length > 0 ||
      discovery.enrolledPrograms.length > 0 ||
      discovery.totalPotentialAnnualValue > 0
    );
  }

  get potentialLrapAmount() {
    return this.lrapDiscovery?.totalPotentialAnnualValue ?? 0;
  }

  get lrapEligibleCount() {
    return this.lrapDiscovery?.eligiblePrograms.length ?? 0;
  }

  get lrapEnrolledCount() {
    return this.lrapDiscovery?.enrolledPrograms.length ?? 0;
  }

  get hasOutsideFundsConflicts() {
    return (this.lrapDiscovery?.outsideFundsConflicts.length ?? 0) > 0;
  }

  formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{#if this.showLoans}}
        {{#if this.hasLinkedLoans}}
          {{! Hero Banner }}
          <DataIndicator
            @source={{this.studentLoansSource}}
            @showBadge={{true}}
            @dataSourceId="student-loans"
          >
            <DebtJourneyHero
              @currentBalance={{this.totalBalance}}
              @originalBalance={{this.totalOriginalBalance}}
              @estimatedPayoffDate="2029-08-15"
              @yearProgressAmount={{this.yearProgressAmount}}
              @yearOverYearImprovement={{34}}
            />
          </DataIndicator>

          {{! LRAP Opportunity Banner }}
          {{#if this.hasLrapOpportunity}}
            <DataIndicator
              @source={{this.lrapSource}}
              @showBadge={{true}}
              @dataSourceId="lrap-opportunity"
              class="mt-6"
            >
              <div
                class="bg-gradient-to-r from-violet-50 via-purple-50 to-indigo-50 border border-violet-200 rounded-xl p-5"
              >
                <div class="flex items-start gap-4">
                  <div class="p-3 bg-violet-100 rounded-xl flex-shrink-0">
                    <PhMagnifyingGlass @size={{28}} class="text-violet-600" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between flex-wrap gap-4">
                      <div>
                        <Heading @level={{3}} class="text-lg font-semibold text-violet-900">
                          Additional Assistance Available!
                        </Heading>
                        <Text class="text-violet-700 mt-1">
                          Based on your employer and loan profile, you may qualify for
                          {{#if this.lrapEnrolledCount}}
                            {{#if this.lrapEligibleCount}}
                              {{this.lrapEligibleCount}}
                              more loan repayment assistance programs.
                            {{else}}
                              ongoing assistance through LRAP programs.
                            {{/if}}
                          {{else}}
                            <strong>{{this.formatCurrency this.potentialLrapAmount}}/year</strong>
                            in Loan Repayment Assistance Programs.
                          {{/if}}
                        </Text>

                        {{#if this.hasOutsideFundsConflicts}}
                          <div
                            class="flex items-center gap-2 mt-2 text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg w-fit"
                          >
                            <PhWarning @size={{16}} />
                            <Text class="text-sm">
                              Optimization opportunity: Some programs have "outside funds" rules
                            </Text>
                          </div>
                        {{/if}}
                      </div>

                      <div class="flex items-center gap-3">
                        {{#if this.lrapEnrolledCount}}
                          <Chip @intent="success" @size="sm">
                            {{this.lrapEnrolledCount}}
                            Enrolled
                          </Chip>
                        {{/if}}
                        {{#if this.lrapEligibleCount}}
                          <Chip @intent="primary" @size="sm">
                            {{this.lrapEligibleCount}}
                            Eligible
                          </Chip>
                        {{/if}}
                      </div>
                    </div>

                    <div class="flex items-center gap-3 mt-4">
                      <LinkTo @route="authenticated.reimagined.lrap">
                        <Button
                          @intent="primary"
                          @size="sm"
                          @icon={{PhArrowRight}}
                          @iconPlacement="end"
                          @label="Discover Programs"
                        />
                      </LinkTo>
                      {{#if this.hasOutsideFundsConflicts}}
                        <LinkTo @route="authenticated.reimagined.finance.optimizer">
                          <Button @intent="default" @size="sm" @appearance="outlined">
                            View Optimizer
                          </Button>
                        </LinkTo>
                      {{/if}}
                    </div>
                  </div>
                </div>
              </div>
            </DataIndicator>
          {{/if}}

          {{! Key Metrics }}
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
            <DataIndicator
              @source={{this.studentLoansSource}}
              @showBadge={{true}}
              @dataSourceId="student-loans"
            >
              <StatCard
                @icon={{PhBank}}
                @label="Total Balance"
                @value={{this.formatCurrency this.totalBalance}}
                @color="indigo"
              >
                <:extra>
                  <div class="flex items-center gap-1 text-emerald-600 text-sm">
                    <PhTrendUp @size={{14}} />
                    <span>↓ {{this.formatCurrency this.yearProgressAmount}} this year</span>
                  </div>
                </:extra>
              </StatCard>
            </DataIndicator>

            <DataIndicator
              @source={{this.studentLoansSource}}
              @showBadge={{true}}
              @dataSourceId="student-loans"
            >
              <StatCard
                @icon={{PhCalendar}}
                @label="Monthly Payment"
                @value="{{this.formatCurrency this.totalMonthlyPayment}}/mo"
                @subtext={{this.nextPaymentSubtext}}
                @color="blue"
              />
            </DataIndicator>

            <DataIndicator
              @source={{this.contributionsSource}}
              @showBadge={{true}}
              @dataSourceId="slr-contributions"
            >
              <StatCard
                @icon={{PhHandshake}}
                @label="Employer Help"
                @value="{{this.formatCurrency this.employerContribution}}/mo"
                @subtext="= $1,500/year tax-free!"
                @color="green"
                @valueClass="text-emerald-600"
              />
            </DataIndicator>

            <DataIndicator
              @source={{this.studentLoansSource}}
              @showBadge={{true}}
              @dataSourceId="student-loans"
            >
              <StatCard
                @icon={{PhPercent}}
                @label="Avg. Interest Rate"
                @value="{{this.averageInterestRate}}%"
                @color="amber"
              >
                <:extra>
                  <div class="flex items-center gap-1 text-amber-600 text-sm">
                    <PhWarning @size={{14}} />
                    <span>Refinance may lower this</span>
                  </div>
                </:extra>
              </StatCard>
            </DataIndicator>
          </div>

          {{! Smart Actions }}
          <DataIndicator
            @source={{this.recommendationsSource}}
            @showBadge={{true}}
            @dataSourceId="ai-recommendations"
            class="mt-6"
          >
            <SectionHeader
              @icon={{PhLightbulb}}
              @title="Smart Actions"
              @iconClass="text-amber-500"
              @iconSize={{24}}
            />
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              {{#each this.actions as |smartAction|}}
                <SmartActionCard @item={{smartAction}} />
              {{/each}}
            </div>
          </DataIndicator>

          {{! Loan Accounts }}
          <div class="mt-6">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <PhBank @size={{24}} class="text-gray-600" />
                <Heading @level={{2}} class="text-lg font-semibold text-gray-900">Your Loan Accounts</Heading>
              </div>
              <LinkTo
                @route="authenticated.observability.upload"
                class="flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
              >
                <span>Add Account</span>
                <PhArrowRight @size={{16}} />
              </LinkTo>
            </div>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {{#each this.loans as |loan index|}}
                <LoanProgressCard @loan={{loan}} @featured={{eq index 0}} />
              {{/each}}
            </div>
          </div>

          {{! Milestones }}
          <DataIndicator
            @source={{this.milestonesSource}}
            @showBadge={{true}}
            @dataSourceId="debt-milestones"
            class="mt-6"
          >
            <MilestoneTracker
              @milestones={{this.milestones}}
              @currentProgress={{this.progressPercent}}
            />
          </DataIndicator>

          {{! Financial Tools }}
          <div class="mt-6">
            <SectionHeader
              @icon={{PhChartLineUp}}
              @title="Financial Tools"
              @iconClass="text-blue-600"
              @iconSize={{24}}
            />
            <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
              <ToolCard
                @route="authenticated.repaying-student-debt.repayment-strategy-finder"
                @icon={{PhTarget}}
                @title="Strategy Finder"
                @subtitle="Best repayment path"
                @color="indigo"
              />
              <ToolCard
                @route="authenticated.reimagined.coaches"
                @icon={{PhChatCircleDots}}
                @title="Loan Coaches"
                @subtitle="Expert guidance"
                @color="teal"
              />
              <ToolCard
                @route="authenticated.reimagined.glossary"
                @icon={{PhBookOpen}}
                @title="Glossary"
                @subtitle="Learn the terms"
                @color="blue"
              />
              <ToolCard
                @route="authenticated.reimagined.finance.optimizer"
                @icon={{PhSparkle}}
                @title="127 Optimizer"
                @subtitle="Tax-free allocation"
                @color="emerald"
              />
              <ToolCard
                @route="authenticated.idr.dashboard"
                @icon={{PhCurrencyDollar}}
                @title="Income-Driven"
                @subtitle="Lower payments"
                @color="amber"
              />
            </div>
          </div>

          {{! Employer Banner }}
          {{#if this.isDemoMode}}
            <DataIndicator
              @source={{this.contributionsSource}}
              @showBadge={{true}}
              @dataSourceId="slr-contributions"
              class="mt-6"
            >
              <div class="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
                <div class="flex items-center justify-between flex-wrap gap-4">
                  <div class="flex items-center gap-4">
                    <div class="p-3 bg-white/20 rounded-lg">
                      <PhHandshake @size={{28}} />
                    </div>
                    <div>
                      <Heading @level={{3}} class="text-white text-lg font-semibold">
                        {{this.employerName}}
                        has contributed $4,850 to your loans
                      </Heading>
                      <Text class="text-emerald-100">
                        Since you enrolled, your employer has helped accelerate your debt payoff by
                        2 years.
                      </Text>
                    </div>
                  </div>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    class="border-white text-white hover:bg-white/10"
                  >
                    View Contribution History
                  </Button>
                </div>
              </div>
            </DataIndicator>
          {{/if}}
        {{else}}
          {{! NO LOANS LINKED YET }}
          <div
            class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 text-center"
          >
            <div
              class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <PhBank @size={{40}} class="text-blue-600" />
            </div>

            <Heading @level={{1}} class="text-2xl font-bold text-gray-900 mb-3">
              Link Your Student Loans
            </Heading>

            <Text class="text-gray-600 max-w-md mx-auto mb-6">
              Connect your loan servicer to see your balances, track your progress, and start
              receiving employer contributions of up to $125/month!
            </Text>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <Button @intent="primary">
                <span class="flex items-center gap-2">
                  <PhBank @size={{18}} />
                  Connect Loan Servicer
                </span>
              </Button>
              <Button @intent="primary" @appearance="outlined">
                Learn More
              </Button>
            </div>

            <div class="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div class="bg-white rounded-xl p-4">
                <div class="text-2xl font-bold text-blue-600">$125</div>
                <div class="text-sm text-gray-600">Monthly employer contribution</div>
              </div>
              <div class="bg-white rounded-xl p-4">
                <div class="text-2xl font-bold text-emerald-600">2.5 years</div>
                <div class="text-sm text-gray-600">Average payoff acceleration</div>
              </div>
              <div class="bg-white rounded-xl p-4">
                <div class="text-2xl font-bold text-violet-600">$18,000</div>
                <div class="text-sm text-gray-600">Lifetime employer benefit</div>
              </div>
            </div>
          </div>
        {{/if}}
      {{else}}
        {{! LOAN BENEFIT NOT AVAILABLE - Friendly redirect to education benefits }}
        <div
          class="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-2xl p-8 border border-violet-100"
        >
          <div class="max-w-2xl mx-auto text-center">
            <div
              class="w-20 h-20 bg-gradient-to-br from-violet-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <PhGraduationCap @size={{40}} class="text-violet-600" />
            </div>

            <Heading @level={{1}} class="text-2xl font-bold text-gray-900 mb-3">
              Focus on Your Education Instead!
            </Heading>

            <Text class="text-gray-600 mb-8">
              While student loan repayment isn't part of your current benefits package, you have
              access to generous tuition assistance that can help you grow your career and avoid
              future debt.
            </Text>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div class="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                <div class="text-3xl font-bold text-violet-600 mb-1">$5,250</div>
                <div class="text-sm text-gray-600">Annual tuition assistance</div>
              </div>
              <div class="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                <div class="text-3xl font-bold text-emerald-600 mb-1">Tax-Free</div>
                <div class="text-sm text-gray-600">Section 127 benefit</div>
              </div>
              <div class="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                <div class="text-3xl font-bold text-blue-600 mb-1">100+</div>
                <div class="text-sm text-gray-600">Approved programs</div>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <LinkTo
                @route="authenticated.reimagined.benefits"
                class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-violet-600 text-white font-semibold rounded-xl hover:bg-violet-700 transition-colors"
              >
                <PhGraduationCap @size={{20}} />
                <span>Explore Education Benefits</span>
              </LinkTo>
              <LinkTo
                @route="authenticated.reimagined.explore"
                class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-violet-700 font-semibold rounded-xl border border-violet-200 hover:bg-violet-50 transition-colors"
              >
                <PhTarget @size={{20}} />
                <span>Explore Careers</span>
              </LinkTo>
            </div>

            <div class="mt-8 p-4 bg-white/60 rounded-xl border border-violet-100">
              <div class="flex items-center justify-center gap-2 text-sm text-gray-600">
                <PhLightbulb @size={{18}} class="text-amber-500" />
                <span>
                  <strong>Pro tip:</strong>
                  Earning a degree or certification can increase your salary by 20-30%
                </span>
              </div>
            </div>
          </div>
        </div>
      {{/if}}
    </VStack>
  </template>
}
